/**
 * Birthday Club sign-up form.
 *
 * On submit this does two calls to Klaviyo's public, client-side API
 * (safe to call from the browser — it only ever accepts your PUBLIC
 * API key, never a private one):
 *
 *   1. POST /client/profiles      -> saves name / phone / birthday as
 *                                     profile properties
 *   2. POST /client/subscriptions -> opts the profile into email + SMS
 *                                     marketing and adds them to your
 *                                     Birthday Club list
 *
 * Docs:
 *   https://developers.klaviyo.com/en/reference/create_client_profile
 *   https://developers.klaviyo.com/en/reference/create_client_subscription
 *
 * Duplicate sign-ups: Klaviyo treats email AND phone number as profile
 * identifiers, so submitting again with a new email but the same phone
 * updates the existing profile rather than creating a new one. This
 * form also sets a `birthday_club_joined` custom property the first
 * time someone joins — use that property to filter a Klaviyo flow so a
 * repeat sign-up doesn't re-trigger a fresh birthday-gift automation.
 */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2026-07-15';

  function init() {
    var form = document.getElementById('birthday-club-form');
    if (!form) return;

    var errorEl = form.querySelector('.birthday-club__error');
    var successEl = form.closest('.birthday-club__inner').querySelector('.birthday-club__success');
    var submitBtn = form.querySelector('.birthday-club__submit');

    var companyId = form.dataset.companyId;
    var listId = form.dataset.listId;
    var defaultCountryCode = form.dataset.countryCode || '+61';

    // Remember on this device/browser that the visitor already joined,
    // purely for a nicer UX (skip straight to the success message).
    try {
      if (localStorage.getItem('birthdayClubJoined') === '1') {
        showSuccess();
      }
    } catch (e) {
      /* localStorage unavailable — no-op */
    }

    form.addEventListener('submit', handleSubmit);

    function handleSubmit(event) {
      event.preventDefault();
      hideError();

      // Honeypot: if this hidden field got filled in, silently drop it.
      var honeypot = form.querySelector('#bc-company');
      if (honeypot && honeypot.value) {
        return;
      }

      if (!companyId || !listId) {
        showError('This form is not fully configured yet — add your Klaviyo public API key and list ID in the theme editor.');
        return;
      }

      var data = collectFormData();
      var validationError = validate(data);
      if (validationError) {
        showError(validationError);
        return;
      }

      setLoading(true);

      upsertProfile(data)
        .then(function () {
          return subscribeProfile(data);
        })
        .then(function () {
          try {
            localStorage.setItem('birthdayClubJoined', '1');
          } catch (e) {
            /* no-op */
          }
          setLoading(false);
          showSuccess();
        })
        .catch(function (err) {
          console.error('Birthday club sign-up failed', err);
          setLoading(false);
          showError('Something went wrong submitting the form. Please try again in a moment.');
        });
    }

    function collectFormData() {
      var formData = new FormData(form);
      return {
        firstName: (formData.get('first_name') || '').toString().trim(),
        lastName: (formData.get('last_name') || '').toString().trim(),
        email: (formData.get('email') || '').toString().trim(),
        phoneRaw: (formData.get('phone') || '').toString().trim(),
        dobDay: formData.get('dob_day'),
        dobMonth: formData.get('dob_month'),
        dobYear: formData.get('dob_year'),
        consent: formData.get('consent') === 'on'
      };
    }

    function validate(data) {
      if (!data.firstName) return 'Please enter your first name.';
      if (!isValidEmail(data.email)) return 'Please enter a valid email address.';

      var phone = normalizePhone(data.phoneRaw, defaultCountryCode);
      if (!phone) return 'Please enter a valid mobile number.';
      data.phone = phone;

      if (!data.dobDay || !data.dobMonth) return 'Please select your day and month of birth.';
      if (!data.consent) return 'Please agree to receive marketing emails and texts to continue.';

      return null;
    }

    function isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    // Normalizes local numbers (e.g. "04XX XXX XXX") to E.164 (e.g.
    // "+614XXXXXXXX") as required by Klaviyo. Adjust this if you sell
    // outside Australia / expect other local formats.
    function normalizePhone(raw, countryCode) {
      var digits = raw.replace(/[^\d+]/g, '');
      if (!digits) return null;

      if (digits.charAt(0) === '+') {
        return /^\+\d{8,15}$/.test(digits) ? digits : null;
      }

      if (digits.charAt(0) === '0') {
        digits = digits.substring(1);
      }

      var candidate = countryCode + digits;
      return /^\+\d{8,15}$/.test(candidate) ? candidate : null;
    }

    function buildBirthdayIso(data) {
      var year = data.dobYear ? parseInt(data.dobYear, 10) : 1904; // leap-safe placeholder when year is withheld
      var month = parseInt(data.dobMonth, 10);
      var day = parseInt(data.dobDay, 10);
      var mm = String(month).padStart(2, '0');
      var dd = String(day).padStart(2, '0');
      return year + '-' + mm + '-' + dd;
    }

    function upsertProfile(data) {
      var properties = {
        birthday_day: parseInt(data.dobDay, 10),
        birthday_month: parseInt(data.dobMonth, 10),
        birthday_club_joined: true,
        birthday_club_joined_at: new Date().toISOString(),
        birthday_club_source: 'Birthday club page'
      };
      if (data.dobYear) {
        properties.birthday_year = parseInt(data.dobYear, 10);
      }
      properties.birthday = buildBirthdayIso(data);

      var body = {
        data: {
          type: 'profile',
          attributes: {
            email: data.email,
            phone_number: data.phone,
            first_name: data.firstName,
            last_name: data.lastName || undefined,
            properties: properties
          }
        }
      };

      return klaviyoFetch('/client/profiles', body);
    }

    function subscribeProfile(data) {
      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Birthday club page',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: data.email,
                  phone_number: data.phone,
                  subscriptions: {
                    email: { marketing: { consent: 'SUBSCRIBED' } },
                    sms: { marketing: { consent: 'SUBSCRIBED' } }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      };

      return klaviyoFetch('/client/subscriptions', body);
    }

    function klaviyoFetch(path, body) {
      var url = 'https://a.klaviyo.com' + path + '?company_id=' + encodeURIComponent(companyId);
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        if (!response.ok && response.status !== 202) {
          throw new Error('Klaviyo request to ' + path + ' failed with status ' + response.status);
        }
        return response;
      });
    }

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      submitBtn.classList.toggle('is-loading', isLoading);
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function hideError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function showSuccess() {
      form.hidden = true;
      successEl.hidden = false;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();